/**
 * The little state one drag at a time needs, shared by every drag source and
 * drop target on the canvas:
 *
 * - which folder is ARMED (hovered for DROP_ARM_DELAY_MS, so passing over a
 *   folder on the way somewhere else is not aiming at it), and whether the
 *   drag would MOVE items into it or UPLOAD files into it;
 * - which items the canvas is dragging right now (a dragged folder never arms
 *   as its own target; the payload itself is unreadable until the drop);
 * - which native drop events a folder already took, so the page-level upload
 *   overlay does not upload the same files a second time.
 */

import { create } from 'zustand';

import type { DraggedItem } from '../../navigation/HierarchyTree.dnd';

export const DROP_ARM_DELAY_MS = 300;

export type DropKind = 'move' | 'upload';

const useArmedStore = create<{ armedId: string | null; armedKind: DropKind | null }>(() => ({
  armedId: null,
  armedKind: null,
}));

let pending: { id: string; timer: ReturnType<typeof setTimeout> } | null = null;
let dragging: ReadonlyArray<DraggedItem> = [];
const handledDrops = new WeakSet<Event>();

/** Whether this folder is the armed drop target (a boolean selector: one tile re-renders). */
export function useIsDropTarget(id: string): boolean {
  return useArmedStore((s) => s.armedId === id);
}

/** What a drop on this folder would do, while it is the armed target; null otherwise. */
export function useDropKind(id: string): DropKind | null {
  return useArmedStore((s) => (s.armedId === id ? s.armedKind : null));
}

/** The armed folder's id, for a view that draws rows from a render callback. */
export function useArmedDropTargetId(): string | null {
  return useArmedStore((s) => s.armedId);
}

export function armedDropTargetId(): string | null {
  return useArmedStore.getState().armedId;
}

/** Hovering `id`: arm it after the delay, unless it is already armed or pending. */
export function hoverDropTarget(id: string, kind: DropKind = 'move'): void {
  if (dragging.some((item) => item.id === id)) return;
  if (useArmedStore.getState().armedId === id || pending?.id === id) return;
  resetDropTarget();
  pending = {
    id,
    timer: setTimeout(() => {
      pending = null;
      useArmedStore.setState({ armedId: id, armedKind: kind });
    }, DROP_ARM_DELAY_MS),
  };
}

/** The pointer left `id`: disarm it (a different target may already be armed). */
export function leaveDropTarget(id: string): void {
  if (pending?.id === id) {
    clearTimeout(pending.timer);
    pending = null;
  }
  if (useArmedStore.getState().armedId === id)
    useArmedStore.setState({ armedId: null, armedKind: null });
}

export function resetDropTarget(): void {
  if (pending) clearTimeout(pending.timer);
  pending = null;
  if (useArmedStore.getState().armedId !== null)
    useArmedStore.setState({ armedId: null, armedKind: null });
}

/** The items the canvas is dragging (empty when none, or for a tree drag). */
export function setDraggingItems(items: ReadonlyArray<DraggedItem>): void {
  dragging = items;
}

/** Readable during dragover, unlike the drag payload, so a target can refuse early. */
export function draggingItems(): ReadonlyArray<DraggedItem> {
  return dragging;
}

export function markDropHandled(event: Event): void {
  handledDrops.add(event);
}

export function isDropHandled(event: Event): boolean {
  return handledDrops.has(event);
}
